import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Checkbox,
  Fade,
  FormControlLabel,
  IconButton,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { Close } from "@material-ui/icons";
import { getUsers } from "../../../store/hub/users";
import { addSnackbarIssue } from "../../../components/common/StoreCommon";
import {
  apiSetHrLeaveAccess,
  getHrLeaveAccess,
} from "../../../components/common/ApiMiddleware";

export default function HrLeaveAccessDialog({ open, onClose }) {
  const classes = useStyles();
  const users = useSelector(getUsers)();

  const [selectedUserNames, setSelectedUserNames] = useState(new Set());
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [accessSnapshot, setAccessSnapshot] = useState(null);
  const [selectionReady, setSelectionReady] = useState(false);

  const sortedUsers = useMemo(() => {
    return [...(users || [])]
      .filter((user) => user?.UserName)
      .sort((a, b) => {
        const nameA = `${a.FirstName || ""} ${a.LastName || ""}`.trim();
        const nameB = `${b.FirstName || ""} ${b.LastName || ""}`.trim();
        return nameA.localeCompare(nameB);
      });
  }, [users]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedUsers;
    return sortedUsers.filter((user) => {
      const fullName = `${user.FirstName || ""} ${user.LastName || ""}`
        .trim()
        .toLowerCase();
      const userName = (user.UserName || "").toLowerCase();
      return fullName.includes(term) || userName.includes(term);
    });
  }, [sortedUsers, search]);

  useEffect(() => {
    if (!open) {
      setAccessSnapshot(null);
      setSelectionReady(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setLoadError("");
    setSearch("");
    setSelectedUserNames(new Set());
    setSelectionReady(false);
    setAccessSnapshot(null);

    getHrLeaveAccess()
      .then((response) => {
        if (cancelled) return;
        setAccessSnapshot({
          allHaveAccess: Boolean(response?.AllUsersHaveAccess),
          previewNames: (response?.UserNames || []).map((name) =>
            String(name).trim().toLowerCase()
          ),
        });
      })
      .catch(() => {
        if (cancelled) return;
        setLoadError("Unable to load access settings");
        addSnackbarIssue("Unable to load access settings", "warning");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open]);

  useEffect(() => {
    if (!open || !accessSnapshot || selectionReady) return;
    if (sortedUsers.length === 0) return;

    const next = new Set();
    sortedUsers.forEach((user) => {
      const userName = (user.UserName || "").trim();
      if (!userName) return;
      if (
        accessSnapshot.allHaveAccess ||
        accessSnapshot.previewNames.includes(userName.toLowerCase())
      ) {
        next.add(userName);
      }
    });
    setSelectedUserNames(next);
    setSelectionReady(true);
  }, [open, accessSnapshot, selectionReady, sortedUsers]);

  const toggleUser = (userName) => {
    setSelectedUserNames((prev) => {
      const next = new Set(prev);
      if (next.has(userName)) next.delete(userName);
      else next.add(userName);
      return next;
    });
  };

  const handleSave = async () => {
    if (selectedUserNames.size === 0) {
      addSnackbarIssue(
        "Select at least one user, or select all to grant everyone access",
        "warning"
      );
      return;
    }

    setSaving(true);
    try {
      const allSelected =
        sortedUsers.length > 0 &&
        sortedUsers.every((user) => selectedUserNames.has(user.UserName));
      const userNames = allSelected
        ? ""
        : sortedUsers
            .filter((user) => selectedUserNames.has(user.UserName))
            .map((user) => user.UserName)
            .join(",");

      await apiSetHrLeaveAccess(userNames);
      addSnackbarIssue("Access settings saved", "success", false, 5000);
      onClose();
    } catch (e) {
      addSnackbarIssue("Unable to save access settings", "warning");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <Fade in={open}>
        <Paper className={classes.paper}>
          <Box className={classes.header}>
            <Typography variant="h6">Access</Typography>
            <IconButton size="small" onClick={onClose}>
              <Close />
            </IconButton>
          </Box>

          <Typography variant="body2" color="textSecondary" className={classes.help}>
            Choose who can access HR Leave. Selecting all users grants access to
            everyone.
          </Typography>

          <Box className={classes.toolbar}>
            <TextField
              size="small"
              variant="outlined"
              placeholder="Search users"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className={classes.search}
            />
          </Box>

          {loadError ? (
            <Typography variant="body2" className={classes.error}>
              {loadError}
            </Typography>
          ) : null}

          <Box className={classes.list}>
            {loading ? (
              <Typography variant="body2" color="textSecondary">
                Loading...
              </Typography>
            ) : filteredUsers.length === 0 ? (
              <Typography variant="body2" color="textSecondary">
                No users found
              </Typography>
            ) : (
              filteredUsers.map((user) => (
                <FormControlLabel
                  key={user.Id}
                  className={classes.row}
                  control={
                    <Checkbox
                      color="primary"
                      checked={selectedUserNames.has(user.UserName)}
                      onChange={() => toggleUser(user.UserName)}
                    />
                  }
                  label={`${user.FirstName || ""} ${user.LastName || ""}`.trim()}
                />
              ))
            )}
          </Box>

          <Box className={classes.actions}>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSave}
              disabled={saving || loading || Boolean(loadError)}
            >
              Save
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
}

const useStyles = makeStyles((theme) => ({
  modal: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  paper: {
    width: "90%",
    maxWidth: 560,
    maxHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    padding: theme.spacing(2),
    outline: "none",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: theme.spacing(1),
  },
  help: {
    marginBottom: theme.spacing(1.5),
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  search: {
    flexGrow: 1,
  },
  list: {
    flexGrow: 1,
    overflow: "auto",
    maxHeight: "50vh",
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: theme.spacing(1, 1.5),
  },
  row: {
    display: "flex",
    marginLeft: 0,
    marginRight: 0,
    width: "100%",
  },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
  },
  error: {
    color: theme.palette.error.main,
    marginBottom: theme.spacing(1),
  },
}));
